import React, { useEffect, useRef } from 'react'
import {
  Box,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react'
import { StarIcon, WarningIcon } from '@chakra-ui/icons'
import { PRM_Benchmark, PRM_Benchmark_Contest, PRM_Error } from '../types/unified_models'
import Chart from 'chart.js/auto'

interface BenchmarkModuleProps {
  benchmark: PRM_Benchmark | PRM_Benchmark_Contest | PRM_Error | null
  // When provided, the module can filter to a single input name
  activeInput?: string | null
  // If true, hide other inputs and only show activeInput
  limitToActiveInput?: boolean
}

export default function BenchmarkModule({ benchmark, activeInput, limitToActiveInput = false }: BenchmarkModuleProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<Chart | null>(null)

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
      }
    }
  }, [])

  // Chart creation effect - supports optional single-input filtering
  useEffect(() => {
    if (!benchmark || 'error' in benchmark) {
      return
    }

    const benchmarkData = benchmark as PRM_Benchmark | PRM_Benchmark_Contest
    
    // Normalize to arrays
    const refArray: any[] = Array.isArray(benchmarkData.reference)
      ? (benchmarkData.reference as any[])
      : benchmarkData.reference ? [benchmarkData.reference] : []
    const subArray: any[] = Array.isArray(benchmarkData.submission)
      ? (benchmarkData.submission as any[])
      : benchmarkData.submission ? [benchmarkData.submission] : []
    const challArray: any[] = Array.isArray(benchmarkData.challenges)
      ? (benchmarkData.challenges as any[])
      : benchmarkData.challenges ? [benchmarkData.challenges] : []

    // Determine if we should filter to a single input, with robust matching
    const inferredSingleInput = subArray.length === 1 && refArray.length > 1
    const filterRequested = Boolean(limitToActiveInput && activeInput)
    const initialCandidate: string | null = filterRequested ? activeInput! : (inferredSingleInput ? subArray[0]?.benchmark_name : null)

    const matches = (name?: string, target?: string) => {
      if (!name || !target) return false
      const a = String(name).toLowerCase()
      const b = String(target).toLowerCase()
      return a === b || a.includes(b) || b.includes(a)
    }

    // Try match by requested candidate; if not found and we have one submission, match by it
    const resolveCandidate = (): string | null => {
      if (initialCandidate) {
        const found = refArray.some(r => matches(r.benchmark_name, initialCandidate)) ||
                      subArray.some(s => matches(s.benchmark_name, initialCandidate))
        if (found) return initialCandidate
      }
      if (subArray.length === 1) {
        return subArray[0]?.benchmark_name || null
      }
      return null
    }

    const effectiveCandidate = resolveCandidate()
    const doFilter = Boolean(effectiveCandidate)

    const filteredRefs = doFilter && effectiveCandidate ? refArray.filter(r => matches(r.benchmark_name, effectiveCandidate)) : refArray
    const filteredSubs = doFilter && effectiveCandidate ? subArray.filter(s => matches(s.benchmark_name, effectiveCandidate)) : subArray
    const filteredChalls = doFilter && effectiveCandidate ? challArray.filter(c => matches(c.benchmark_name, effectiveCandidate)) : challArray
    
    // Destroy existing chart before creating new one
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
      chartInstanceRef.current = null
    }

    if (!chartRef.current) return

    const ctx = chartRef.current.getContext('2d')
    if (!ctx) return

    // Check if we have multiple benchmark results after filtering
    const hasMultipleResults = filteredRefs.length > 1 && filteredSubs.length > 0

    let labels: string[] = []
    let data: number[] = []
    let title = ''
    let hasValidData = false

    if (hasMultipleResults && !doFilter) {
      // For multiple inputs, show geometric mean speedup for each implementation
      const implementations = getAllImplementations()
      
      labels = []
      data = []
      
      implementations.forEach(impl => {
        const geomean = calculateGeometricMeanSpeedup(impl)
        if (geomean !== null) {
          let displayName = impl
          if (impl === 'reference') displayName = 'Reference'
          else if (impl === 'submission') displayName = 'Your Submission'
          else displayName = `Challenge-${impl}`
          
          labels.push(displayName)
          data.push(geomean * 100)
        }
      })
      
      title = 'Geometric Mean Speedup over Reference (%)'
      hasValidData = data.length > 0
    } else {
      // Single-input view (explicitly filtered or legacy format)
      const reference = filteredRefs[0]
      const submission = filteredSubs[0]
      const challenges = filteredChalls

      if (reference && reference.runtime > 0) {
        labels = ['Reference']
        data = [100]

        challenges.forEach((ch: any, idx: number) => {
          if (ch.runtime > 0) {
            labels.push(`Challenge-${ch.function || idx + 1}`)
            data.push((reference.runtime / ch.runtime) * 100)
          }
        })

        if (submission && submission.runtime > 0) {
          labels.push('Your Submission')
          data.push((reference.runtime / submission.runtime) * 100)
        }
        title = 'Speedup over Reference (%)'
        hasValidData = data.length > 0
      }
    }

    // Only create chart if we have valid data
    if (!hasValidData) {
      return
    }

    // Create the chart
    chartInstanceRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: title,
          data: data,
          backgroundColor: data.map((_, index) => {
            const label = labels[index]?.toLowerCase() || ''
            if (label === 'reference') return 'rgba(128, 128, 128, 0.8)' // Reference - gray
            if (label.includes('your submission') || label.endsWith('you')) return 'rgba(34, 197, 94, 0.8)' // Your submission - green
            if (label.includes('challenge')) return 'rgba(59, 130, 246, 0.8)' // Challenges - blue
            return 'rgba(168, 85, 247, 0.8)' // Other submissions - purple
          }),
          borderColor: data.map((_, index) => {
            const label = labels[index]?.toLowerCase() || ''
            if (label === 'reference') return 'rgba(128, 128, 128, 1)'
            if (label.includes('your submission') || label.endsWith('you')) return 'rgba(34, 197, 94, 1)'
            if (label.includes('challenge')) return 'rgba(59, 130, 246, 1)'
            return 'rgba(168, 85, 247, 1)'
          }),
          borderWidth: 1,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: true,
            text: title,
            font: {
              size: 14,
              weight: 'bold',
            },
          },
        },
        scales: {
          x: {
            ticks: {
              font: {
                size: 10,
              },
            },
          },
          y: {
            display: true,
            title: {
              display: true,
              text: title.includes('Speedup') ? 'Speedup over reference' : 'Runtime (seconds)',
              font: {
                size: 12,
                weight: 'bold',
              },
            },
            ticks: {
              callback: function(value: any) {
                if (title.includes('Speedup')) {
                  return value + '%'
                }
                return value
              },
              font: {
                size: 10,
              },
            },
          },
        },
      },
    })
  }, [benchmark, activeInput, limitToActiveInput])

  if (!benchmark) {
    return (
      <VStack align="stretch" spacing={3} w="100%" maxW="100%">
        <HStack>
          <Icon as={StarIcon} color="gray.400" />
          <Heading size="sm" color={textColor}>Benchmark Results</Heading>
        </HStack>
        <Text fontSize="sm" color={textColor}>Benchmarks have not yet been run.</Text>
      </VStack>
    )
  }

  // Check if it's an error response
  if ('error' in benchmark && benchmark.error) {
    return (
      <VStack align="stretch" spacing={3} w="100%" maxW="100%">
        <HStack>
          <Icon as={WarningIcon} color="red.400" />
          <Heading size="sm" color={textColor}>Benchmark Results</Heading>
        </HStack>
        <Text fontWeight="bold" color="red.500" fontSize="sm">
          Failed to run benchmarks.
        </Text>
        {benchmark.stdout && (
          <Box>
            <Text fontWeight="medium" fontSize="sm" color={textColor}>Standard Out:</Text>
            <Box
              as="pre"
              bg="gray.100"
              p={2}
              borderRadius="md"
              fontSize="xs"
              overflowX="auto"
              maxH="200px"
            >
              {benchmark.stdout}
            </Box>
          </Box>
        )}
        {benchmark.stderr && (
          <Box>
            <Text fontWeight="medium" fontSize="sm" color={textColor}>Standard Error:</Text>
            <Box
              as="pre"
              bg="gray.100"
              p={2}
              borderRadius="md"
              fontSize="xs"
              overflowX="auto"
              maxH="200px"
            >
              {benchmark.stderr}
            </Box>
          </Box>
        )}
      </VStack>
    )
  }

  // At this point, benchmark is guaranteed to be PRM_Benchmark or PRM_Benchmark_Contest
  const benchmarkData = benchmark as PRM_Benchmark | PRM_Benchmark_Contest

  // Helper function to format runtime values
  const formatRuntime = (runtime: number): { value: string; unit: string } => {
    if (runtime < 0.001) {
      return { value: (runtime * 1000000).toFixed(2), unit: 'μs' }
    } else if (runtime < 1) {
      return { value: (runtime * 1000).toFixed(2), unit: 'ms' }
    } else {
      return { value: runtime.toFixed(3), unit: 's' }
    }
  }

  // Previously used for a removed column

  // Helper function to calculate geometric mean speedup for any implementation across all inputs
  const calculateGeometricMeanSpeedup = (implementationName: string) => {
    if (!Array.isArray(benchmarkData.reference) || !Array.isArray(benchmarkData.submission)) {
      return null
    }
    
    const speedups: number[] = []
    
    // Get speedups for this implementation across all inputs
    benchmarkData.reference.forEach(ref => {
      if (implementationName === 'reference') {
        speedups.push(1.0) // Reference is always 1.0x
      } else if (implementationName === 'submission') {
        const sub = (benchmarkData.submission as any[]).find(s => s.benchmark_name === ref.benchmark_name)
        if (sub && ref.runtime > 0 && sub.runtime > 0) {
          speedups.push(ref.runtime / sub.runtime)
        }
      } else {
        // It's a challenge implementation
        const challenge = benchmarkData.challenges?.find((c: any) => 
          c.benchmark_name === ref.benchmark_name && 
          (c.function === implementationName || `Challenge-${c.function}` === implementationName)
        )
        if (challenge && ref.runtime > 0 && challenge.runtime > 0) {
          speedups.push(ref.runtime / challenge.runtime)
        }
      }
    })
    
    if (speedups.length === 0) return null
    
    return Math.pow(speedups.reduce((a, b) => a * b, 1), 1 / speedups.length)
  }
  
  // Helper function to get all unique implementations across all inputs
  const getAllImplementations = () => {
    const implementations = new Set(['reference', 'submission'])
    
    // Add all unique challenge functions
    benchmarkData.challenges?.forEach((challenge: any) => {
      if (challenge.function) {
        implementations.add(challenge.function)
      }
    })
    
    return Array.from(implementations)
  }
  
  // Helper function to calculate aggregate performance metrics for multiple inputs
  const calculateAggregateMetrics = (refResults: any[], subResults: any[]) => {
    if (!Array.isArray(refResults) || !Array.isArray(subResults)) {
      return null
    }
    
    const speedups: number[] = []
    const runtimes: {ref: number, sub: number}[] = []
    
    subResults.forEach(sub => {
      const ref = refResults.find(r => r.benchmark_name === sub.benchmark_name)
      if (ref && ref.runtime > 0 && sub.runtime > 0) {
        speedups.push(ref.runtime / sub.runtime)
        runtimes.push({ref: ref.runtime, sub: sub.runtime})
      }
    })
    
    if (speedups.length === 0) return null
    
    const avgSpeedup = speedups.reduce((a, b) => a + b, 0) / speedups.length
    const geomeanSpeedup = Math.pow(speedups.reduce((a, b) => a * b, 1), 1 / speedups.length)
    const minSpeedup = Math.min(...speedups)
    const maxSpeedup = Math.max(...speedups)
    
    return {
      avgSpeedup: avgSpeedup * 100,
      geomeanSpeedup: geomeanSpeedup * 100,
      minSpeedup: minSpeedup * 100,
      maxSpeedup: maxSpeedup * 100,
      inputCount: speedups.length
    }
  }
  const refArrayAll: any[] = Array.isArray(benchmarkData.reference) ? (benchmarkData.reference as any[]) : []
  const subArrayAll: any[] = Array.isArray(benchmarkData.submission) ? (benchmarkData.submission as any[]) : []
  const challArrayAll: any[] = Array.isArray(benchmarkData.challenges) ? (benchmarkData.challenges as any[]) : []
  const inferredSingleInputView = subArrayAll.length === 1 && refArrayAll.length > 1
  const filterRequestedView = Boolean(limitToActiveInput && activeInput)
  const initialCandidateView: string | null = filterRequestedView ? activeInput! : (inferredSingleInputView ? subArrayAll[0]?.benchmark_name : null)
  const matchesView = (name?: string, target?: string) => {
    if (!name || !target) return false
    const a = String(name).toLowerCase()
    const b = String(target).toLowerCase()
    return a === b || a.includes(b) || b.includes(a)
  }
  const resolveCandidateView = (): string | null => {
    if (initialCandidateView) {
      const found = refArrayAll.some(r => matchesView(r.benchmark_name, initialCandidateView)) ||
                    subArrayAll.some(s => matchesView(s.benchmark_name, initialCandidateView))
      if (found) return initialCandidateView
    }
    if (subArrayAll.length === 1) {
      return subArrayAll[0]?.benchmark_name || null
    }
    return null
  }
  const activeInputForView: string | null = resolveCandidateView()
  const doFilterView = Boolean(activeInputForView)
  const filteredRefsView = doFilterView && activeInputForView ? refArrayAll.filter(r => matchesView(r.benchmark_name, activeInputForView)) : refArrayAll
  const filteredSubsView = doFilterView && activeInputForView ? subArrayAll.filter(s => matchesView(s.benchmark_name, activeInputForView)) : subArrayAll
  const filteredChallsView = doFilterView && activeInputForView ? challArrayAll.filter(c => matchesView(c.benchmark_name, activeInputForView)) : challArrayAll
  const hasMultipleInputs = filteredRefsView.length > 1 && filteredSubsView.length > 0
  
  // Calculate aggregate metrics using filtered results
  const aggregateMetrics = calculateAggregateMetrics(filteredRefsView, filteredSubsView)

  return (
    <VStack align="stretch" spacing={3} w="100%" maxW="100%" minW={0}>
      <HStack>
        <Icon as={StarIcon} color="yellow.400" />
        <Heading size="sm" color={textColor}>Benchmark Results</Heading>
      </HStack>

      {/* Mode banner */}
      {doFilterView && activeInputForView && (
        <Box fontSize="xs" color={textColor}>
          Showing results for input: <Badge colorScheme="blue">{activeInputForView}</Badge>
        </Box>
      )}

      {/* Chart */}
      <Box h="180px" w="100%" position="relative" overflow="hidden">
        <canvas ref={chartRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      </Box>

      {/* Geometric Mean Summary Table for Multiple Inputs */}
      {hasMultipleInputs && !doFilterView && (
        <VStack align="stretch" spacing={2} w="100%" maxW="100%" minW={0}>
          <Text fontWeight="medium" fontSize="sm" color={textColor}>Geometric Mean Summary:</Text>
          <TableContainer overflowX="auto" maxW="100%" w="100%">
            <Table size="sm" variant="simple" width="100%" sx={{ tableLayout: 'fixed' }}>
              <Thead>
                <Tr>
                  <Th fontSize="xs" color={textColor}>Solution</Th>
                  <Th fontSize="xs" color={textColor}>Geomean Speedup</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(() => {
                  const implementations = getAllImplementations()
                  const summaryData = implementations.map(impl => {
                    const geomean = calculateGeometricMeanSpeedup(impl)
                    let displayName = impl
                    if (impl === 'reference') displayName = 'Reference'
                    else if (impl === 'submission') displayName = 'Your Submission'
                    else displayName = `Challenge-${impl}`
                    
                    return {
                      name: displayName,
                      type: impl === 'reference' ? 'reference' : impl === 'submission' ? 'submission' : 'challenge',
                      geomean: geomean ? geomean * 100 : 0
                    }
                  }).filter(item => item.geomean > 0)
                    .sort((a, b) => b.geomean - a.geomean) // Sort by geometric mean speedup (best first)
                  
                  return summaryData.map((impl, index) => {
                    const bgColor = impl.type === 'reference' ? 'white' : 
                                   impl.type === 'challenge' ? 'blue.25' : 'green.50'
                    const speedupColor = impl.type === 'submission' ? 
                                        (impl.geomean >= 100 ? 'green.600' : 'red.600') :
                                        impl.type === 'challenge' ?
                                        (impl.geomean >= 100 ? 'blue.600' : 'orange.600') :
                                        textColor
                    
                    return (
                      <Tr key={impl.name} bg={bgColor}>
                        <Td fontSize="xs" color={textColor} fontWeight={impl.type === 'submission' ? 'bold' : 'medium'}>
                          {impl.name} {index === 0 && '🏆'}
                        </Td>
                        <Td fontSize="xs" color={speedupColor} fontWeight={impl.type === 'submission' ? 'bold' : 'normal'}>
                          {impl.geomean.toFixed(1)}%
                        </Td>
                      </Tr>
                    )
                  })
                })()
                }
              </Tbody>
            </Table>
          </TableContainer>
        </VStack>
      )}

      {/* Results Table */}
      <VStack align="stretch" spacing={2} w="100%" maxW="100%" minW={0}>
        <Text fontWeight="medium" fontSize="sm" color={textColor}>Detailed Results {hasMultipleInputs ? 'by Input' : ''} (sorted by runtime):</Text>
          <TableContainer overflowX="auto" maxW="100%" w="100%">
            <Table size="sm" variant="simple" width="100%" sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr>
                <Th fontSize="xs" color={textColor}>Name</Th>
                <Th fontSize="xs" color={textColor}>Runtime</Th>
                <Th fontSize="xs" color={textColor}>Speedup</Th>
              </Tr>
            </Thead>
            <Tbody>
              {hasMultipleInputs ? (
                // Multiple inputs - show all results grouped by input (filtered if needed)
                filteredSubsView.map((sub: any) => {
                  const ref = filteredRefsView.find(r => r.benchmark_name === sub.benchmark_name)
                  if (!ref) return null
                  
                  const speedup = ref.runtime > 0 && sub.runtime > 0 ? (ref.runtime / sub.runtime) * 100 : 0
                  
                  // Find challenges for this specific input
                  const inputChallenges = filteredChallsView.filter((challenge: any) => 
                    challenge.benchmark_name === sub.benchmark_name
                  )
                  
                  // Create array of all implementations for this input and sort by runtime
                  const allImplementations = [
                    { type: 'reference', name: 'Reference', data: ref, speedup: 100, runtime: ref.runtime },
                    ...inputChallenges.map((challenge: any, index: number) => ({
                      type: 'challenge',
                      name: `Challenge-${challenge.function || index + 1}`,
                      data: challenge,
                      speedup: ref.runtime > 0 && challenge.runtime > 0 ? (ref.runtime / challenge.runtime) * 100 : 0,
                      runtime: challenge.runtime
                    })),
                    { type: 'submission', name: 'Your Submission', data: sub, speedup, runtime: sub.runtime }
                  ].sort((a, b) => a.runtime - b.runtime) // Sort by runtime (fastest first)
                  
                  return (
                    <React.Fragment key={sub.benchmark_name}>
                      {/* Input Header */}
                      <Tr bg="blue.50">
                        <Td colSpan={3} fontSize="xs" fontWeight="bold" color="blue.700">
                          Input: {sub.benchmark_name}
                        </Td>
                      </Tr>
                      
                      {/* All implementations sorted by runtime */}
                      {allImplementations.map((impl, index) => {
                        const bgColor = impl.type === 'reference' ? 'white' : 
                                       impl.type === 'challenge' ? 'blue.25' : 'green.50'
                        const fontWeight = impl.type === 'submission' ? 'bold' : 
                                          impl.type === 'reference' ? 'medium' : 'medium'
                        const speedupColor = impl.type === 'submission' ? 
                                            (impl.speedup >= 100 ? 'green.600' : 'red.600') :
                                            impl.type === 'challenge' ?
                                            (impl.speedup >= 100 ? 'blue.600' : 'orange.600') :
                                            textColor
                        
                        return (
                          <Tr key={`${sub.benchmark_name}-${impl.type}-${index}`} bg={bgColor}>
                            <Td fontSize="xs" color={textColor} fontWeight={fontWeight} pl={6}>
                              {impl.name} {index === 0 && '🏆'}
                            </Td>
                            <Td fontSize="xs" color={textColor}>
                              {formatRuntime(impl.data.runtime).value} {formatRuntime(impl.data.runtime).unit}
                            </Td>
                            <Td fontSize="xs" color={speedupColor} fontWeight={impl.type === 'submission' ? 'bold' : 'normal'}>
                              {impl.speedup.toFixed(1)}%
                            </Td>
                          </Tr>
                        )
                      })}
                    </React.Fragment>
                  )
                })
              ) : (
                // Single-input (filtered or inferred)
                (() => {
                  const reference = filteredRefsView[0]
                  const submission = filteredSubsView[0]
                  const challenges = filteredChallsView
                  if (!reference) return null
                  const allImplementations = [
                    { type: 'reference', name: 'Reference', data: reference, speedup: 100, runtime: reference.runtime },
                    ...challenges.map((challenge: any, index: number) => ({
                      type: 'challenge', name: `Challenge-${challenge.function || index + 1}`,
                      data: challenge,
                      speedup: reference.runtime > 0 && challenge.runtime > 0 ? (reference.runtime / challenge.runtime) * 100 : 0,
                      runtime: challenge.runtime
                    })),
                    ...(submission ? [{
                      type: 'submission', name: 'Your Submission', data: submission,
                      speedup: (reference.runtime / submission.runtime) * 100,
                      runtime: submission.runtime
                    }] : [])
                  ].sort((a, b) => a.runtime - b.runtime)
                  return allImplementations.map((impl, index) => {
                    const bgColor = impl.type === 'reference' ? 'white' : impl.type === 'challenge' ? 'blue.25' : 'green.50'
                    const fontWeight = impl.type === 'submission' ? 'bold' : 'medium'
                    const speedupColor = impl.type === 'submission'
                      ? (impl.speedup >= 100 ? 'green.600' : 'red.600')
                      : impl.type === 'challenge'
                      ? (impl.speedup >= 100 ? 'blue.600' : 'orange.600')
                      : textColor
                    return (
                      <Tr key={`${impl.type}-${index}`} bg={bgColor}>
                        <Td fontSize="xs" color={textColor} fontWeight={fontWeight}>
                          {impl.name} {index === 0 && '🏆'}
                        </Td>
                        <Td fontSize="xs" color={textColor}>
                          {formatRuntime(impl.data.runtime).value} {formatRuntime(impl.data.runtime).unit}
                        </Td>
                        <Td fontSize="xs" color={speedupColor} fontWeight={impl.type === 'submission' ? 'bold' : 'normal'}>
                          {impl.speedup.toFixed(1)}%
                        </Td>
                      </Tr>
                    )
                  })
                })()
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </VStack>

      {/* Performance Summary */}
      <VStack align="stretch" spacing={2}>
        <Text fontWeight="medium" fontSize="sm" color={textColor}>Performance Summary:</Text>
        
        {hasMultipleInputs && aggregateMetrics ? (
          // Multiple inputs - show aggregate metrics
          <>
            <HStack justify="space-between" fontSize="xs">
              <Text color={textColor}>Inputs Processed:</Text>
              <Badge colorScheme="blue" fontSize="xs">
                {aggregateMetrics.inputCount}
              </Badge>
            </HStack>
            <HStack justify="space-between" fontSize="xs">
              <Text color={textColor}>Average Speedup:</Text>
              <Badge colorScheme="green" fontSize="xs">
                {aggregateMetrics.avgSpeedup.toFixed(1)}%
              </Badge>
            </HStack>
            <HStack justify="space-between" fontSize="xs">
              <Text color={textColor}>Geometric Mean Speedup:</Text>
              <Badge colorScheme="green" fontSize="xs">
                {aggregateMetrics.geomeanSpeedup.toFixed(1)}%
              </Badge>
            </HStack>
            <HStack justify="space-between" fontSize="xs">
              <Text color={textColor}>Best Speedup:</Text>
              <Badge colorScheme="green" fontSize="xs">
                {aggregateMetrics.maxSpeedup.toFixed(1)}%
              </Badge>
            </HStack>
            <HStack justify="space-between" fontSize="xs">
              <Text color={textColor}>Worst Speedup:</Text>
              <Badge colorScheme={aggregateMetrics.minSpeedup >= 100 ? "green" : "red"} fontSize="xs">
                {aggregateMetrics.minSpeedup.toFixed(1)}%
              </Badge>
            </HStack>
          </>
        ) : (
          // Single input (filtered or inferred) - use filtered view arrays to ensure matching benchmark_name
          (() => {
            const reference = filteredRefsView[0]
            const submission = filteredSubsView[0]
            
            if (!reference || !submission) return null
            
            return (
              <>
                <HStack justify="space-between" fontSize="xs">
                  <Text color={textColor}>Speedup over Reference:</Text>
                  <Badge colorScheme="green" fontSize="xs">
                    {((reference.runtime / submission.runtime) * 100).toFixed(1)}%
                  </Badge>
                </HStack>
                <HStack justify="space-between" fontSize="xs">
                  <Text color={textColor}>Instructions:</Text>
                  <Text color={textColor}>
                    {submission.instructions.toLocaleString()}
                  </Text>
                </HStack>
              </>
            )
          })()
        )}
      </VStack>
    </VStack>
  )
} 